import { watch, nextTick } from 'vue';
import { parseListPageFromQuery } from '@/utils/searchListPagination';
import {
  isMarketplaceHiddenProviderSlug,
  stripHiddenMarketplaceProviderIds,
} from '@/utils/marketplaceProviderFilters';
import {
  applyPbsaFiltersIfResidencias,
  isResidenciasHubRoute,
  PBSA_ES_PROVIDER_IDS,
} from '@/utils/residenciasSearch';

/**
 * Route/region/city watchers that trigger search refreshes.
 */
export function useSearchRouteSync(deps) {
  const {
    route,
    regionId,
    filters,
    selectedLocations,
    selectedTopCity,
    listPage,
    searchResults,
    totalResults,
    showSeoContent,
    isInitializing,
    isLoading,
    isLoadingMapResults,
    mapLoadVersion,
    isStudentMode,
    currentProvider,
    currentCity,
    cityMapping,
    getDefaultSortBy,
    getPriceFromSlug,
    setRegion,
    performSearch,
    ensureDataForListPage,
  } = deps;

  watch(() => searchResults.value.length, () => {
    if (!route.params.city) {
      showSeoContent.value = false;
    }
  });

  watch(() => route.query.page, async (newPage) => {
    if (isInitializing.value) return;
    const target = parseListPageFromQuery(newPage);
    if (target === listPage.value) return;
    listPage.value = target;
    await ensureDataForListPage(target);
  });

  // ─── Watch de ciudad ────────────────────────────────────────────────────
  // immediate: true → el primer disparo ocurre durante setup() (ANTES de onMounted)
  // y solo sincroniza selectedLocations sin llamar a performSearch (onMounted lo hará).
  // Esto corrige la race condition donde, al navegar SPA a /encuentratucoliving/barcelona,
  // el redirect en beforeEnter causaba que el componente montara con route.params.city=undefined
  // (estado intermedio), lanzando una búsqueda sin filtro de ciudad (1789 resultados)
  // seguida de una segunda búsqueda para Barcelona que a veces fallaba con 503.
  let _cityWatchIsFirstFire = true;
  watch(() => route.params.city, (newCity) => {
    filters.value.sortBy = getDefaultSortBy();

    if (_cityWatchIsFirstFire) {
      // Primer disparo (setup): solo actualizar selectedLocations.
      // onMounted llamará a performSearch con el valor ya correcto.
      _cityWatchIsFirstFire = false;
      if (newCity) {
        const normalizedCity = cityMapping[newCity.toLowerCase()];
        if (normalizedCity) selectedLocations.value = [normalizedCity];
      } else {
        selectedLocations.value = [];
      }
      return;
    }

    // Disparos posteriores (cambios de ruta en SPA): actualizar y buscar.
    if (newCity) {
      const normalizedCity = cityMapping[newCity.toLowerCase()];
      if (normalizedCity) {
        selectedLocations.value = [normalizedCity];
        nextTick(() => performSearch());
      }
    } else {
      selectedLocations.value = [];
      nextTick(() => performSearch());
    }
  }, { immediate: true });

  watch(regionId, (newRegion, oldRegion) => {
    if (!isInitializing.value && newRegion !== oldRegion && oldRegion !== undefined) {
      // Reset inmediato: evita que el mapa haga fitBounds con marcadores de la región anterior
      searchResults.value = [];
      totalResults.value = 0;
      mapLoadVersion.value++;
      isLoading.value = true;
      isLoadingMapResults.value = false;

      selectedLocations.value = [];
      filters.value.providers = [];
      if (newRegion === 'us') selectedTopCity.value = 'new-york';
      else if (newRegion === 'uk') selectedTopCity.value = 'london';
      else selectedTopCity.value = null;
      performSearch(true);
    }
  }, { immediate: false });

  watch(selectedTopCity, (newCity, oldCity) => {
    if (!newCity || newCity === oldCity) return;
    const newRegion = newCity === 'new-york' ? 'us' : 'uk';
    if (newRegion !== regionId.value) {
      setRegion(newRegion);
    }
  }, { immediate: false });

  watch(isStudentMode, (isStudent) => {
    if (isInitializing.value) return;
    if (isStudent) {
      if (regionId.value !== 'es') {
        setRegion('es');
      }
      const isPbsa = applyPbsaFiltersIfResidencias(route.path, filters.value);
      if (isPbsa) {
        nextTick(() => performSearch());
      } else if (!filters.value.forStudents) {
        filters.value.forStudents = true;
        nextTick(() => performSearch());
      }
    }
  }, { immediate: true });

  watch(currentProvider, (provider) => {
    if (provider && isMarketplaceHiddenProviderSlug(provider)) {
      filters.value.providers = [];
      return;
    }
    if (provider && !filters.value.providers.includes(provider)) {
      filters.value.providers = [provider];
    }
    // Durante init solo sincronizar filtro; onMounted dispara performSearch con providers listo.
    if (isInitializing.value) return;
    if (provider) {
      nextTick(() => performSearch());
    }
  }, { immediate: true });

  watch(
    () => filters.value.providers,
    (ids) => {
      const cleaned = stripHiddenMarketplaceProviderIds(ids);
      if (cleaned.length !== (ids?.length || 0)) {
        filters.value.providers = cleaned;
      }
      if (isResidenciasHubRoute(route.path)) {
        const current = filters.value.providers || [];
        const hasFullPbsa = PBSA_ES_PROVIDER_IDS.length === current.length
          && PBSA_ES_PROVIDER_IDS.every((id) => current.includes(id));
        if (!hasFullPbsa) {
          filters.value.providers = [...PBSA_ES_PROVIDER_IDS];
        }
      }
    },
    { deep: true },
  );

  watch(() => route.params.priceSlug, (newPriceSlug) => {
    const priceValue = getPriceFromSlug(newPriceSlug);
    if (priceValue !== null) {
      filters.value.maxPrice = priceValue;
    } else if (!route.params.priceSlug) {
      filters.value.maxPrice = null;
    }
    nextTick(() => performSearch());
  }, { immediate: false });
}
